import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { notificar } from '../utils/notify';
import { escapeHtml } from '../utils/formatters';
import '../styles/fmp.css';
import '../styles/Das.css';
import '../styles/Auditoria_balancete.css';

// ── Colunas relevantes ──────────────────────────────────────
const COLS_BALANCETE = [
  { key: 'codi_cta', label: 'Cód.' },
  { key: 'nome_cta', label: 'Conta' },
  { key: 'tipo_cta', label: 'Tipo' },
  { key: 'natureza', label: 'Nat.' },
  { key: 'clas_cta', label: 'Classificação' },
  { key: 'saldoant', label: 'Saldo Anterior', currency: true },
  { key: 'totdeb',   label: 'Total Débitos',  currency: true },
  { key: 'totcre',   label: 'Total Créditos', currency: true },
  { key: 'saldoatu', label: 'Saldo Atual',    currency: true },
];

const COLS_RAZAO = [
  { key: 'codic',           label: 'Cód.' },
  { key: 'nomec',           label: 'Conta' },
  { key: 'datalan',         label: 'Data',     date: true },
  { key: 'historico_excel', label: 'Histórico' },
  { key: 'saldoant',        label: 'Saldo Ant.', currency: true },
  { key: 'valdeb',          label: 'Débito',   currency: true },
  { key: 'valcre',          label: 'Crédito',  currency: true },
  { key: 'saldo',           label: 'Saldo',    currency: true },
  { key: 'natureza',        label: 'Nat.' },
];

const fmtCurrency = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return '-';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (val) => {
  if (!val) return '-';
  if (val instanceof Date) return val.toLocaleDateString('pt-BR');
  if (typeof val === 'number') {
    const d = XLSX.SSF.parse_date_code(val);
    if (d) return `${String(d.d).padStart(2,'0')}/${String(d.m).padStart(2,'0')}/${d.y}`;
  }
  return String(val);
};

function parseXlsx(file, callback) {
  const reader = new FileReader();
  reader.onload = (e) => {
    const data = new Uint8Array(e.target.result);
    const wb = XLSX.read(data, { type: 'array', cellDates: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    callback(XLSX.utils.sheet_to_json(ws, { defval: '' }));
  };
  reader.readAsArrayBuffer(file);
}

// ── Upload Zone ─────────────────────────────────────────────
function UploadZone({ label, faIcon, file, onFile, gradientFrom, accept = '.xls,.xlsx' }) {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);
  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0]; if (f) onFile(f);
  };
  return (
    <div
      className={`audit-upload-zone${dragging ? ' dragging' : ''}${file ? ' uploaded' : ''}`}
      style={{ '--grad-from': gradientFrom }}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current.click()}
    >
      <input ref={inputRef} type="file" accept={accept} style={{ display: 'none' }}
        onChange={(e) => e.target.files[0] && onFile(e.target.files[0])} />
      {file ? (
        <>
          <i className={`fas ${faIcon} fa-2x mb-2`} style={{ color: gradientFrom }}></i>
          <div className="fw-bold text-success mb-1"><i className="fas fa-check-circle me-1"></i>Carregado</div>
          <small className="text-muted">{file.name}</small>
        </>
      ) : (
        <>
          <i className={`fas ${faIcon} fa-3x mb-3`} style={{ color: gradientFrom, opacity: 0.65 }}></i>
          <div className="fw-semibold text-dark mb-1">{label}</div>
          <small className="text-muted">Arraste ou clique — {accept.replace(/\./g, '').replace(/,/g, ' / ').toUpperCase()}</small>
        </>
      )}
    </div>
  );
}

// ── Componente principal ────────────────────────────────────
const Auditoria_balancete = () => {
  const navigate = useNavigate();

  const [balanceteFile, setBalanceteFile] = useState(null);
  const [razaoFile, setRazaoFile]         = useState(null);
  const [balanceteData, setBalanceteData] = useState([]);
  const [razaoData, setRazaoData]         = useState([]);
  const [abaAtiva, setAbaAtiva]           = useState('balancete');
  const [searchBalancete, setSearchBalancete] = useState('');
  const [searchRazao, setSearchRazao]         = useState('');
  const [carregando, setCarregando]           = useState(false);

  // Seletor de cliente
  const [clientes, setClientes]               = useState([]);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [searchCliente, setSearchCliente]     = useState('');
  const [showDropdown, setShowDropdown]       = useState(false);
  const [dropdownPos, setDropdownPos]         = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef                           = useRef(null); // col wrapper (click-outside)
  const listaRef                              = useRef(null); // lista fixa (click-outside)

  // Pré-seleção via localStorage (vindo de Contabilidade)
  const [empresaPendente, setEmpresaPendente] = useState(null);

  // Parâmetros
  const [periodoInicio, setPeriodoInicio] = useState('2025-01-01');
  const [periodoFim, setPeriodoFim]       = useState('2025-12-31');
  const [competenciaInicio, setCompetenciaInicio] = useState('');
  const [competenciaFim, setCompetenciaFim]       = useState('');

  // Recalcula o período (início → fim) a partir das duas competências.
  // Aceita seleção em qualquer ordem: usa a menor como início e a maior como fim.
  useEffect(() => {
    if (!competenciaInicio || !competenciaFim) return;
    const toObj = (valor) => {
      const [mm, yy] = valor.split('/');
      return { mes: parseInt(mm), ano: 2000 + parseInt(yy) };
    };
    const a = toObj(competenciaInicio);
    const b = toObj(competenciaFim);
    const dA = new Date(a.ano, a.mes - 1, 1);
    const dB = new Date(b.ano, b.mes - 1, 1);
    const ini = dA <= dB ? a : b;
    const fim = dA <= dB ? b : a;
    const ultimoDia = new Date(fim.ano, fim.mes, 0).getDate();
    setPeriodoInicio(`${ini.ano}-${String(ini.mes).padStart(2, '0')}-01`);
    setPeriodoFim(`${fim.ano}-${String(fim.mes).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`);
  }, [competenciaInicio, competenciaFim]);

  // Competências: 5 meses antes + mês atual + 5 meses depois, sempre a partir
  // do relógio do sistema (igual para todos os clientes — não depende mais da
  // ultima_entrega). Fixo, calculado uma vez por carregamento da página.
  const opcoesCompetencia = useMemo(() => {
    const hoje = new Date();
    const lista = [];
    for (let i = -5; i <= 5; i++) {
      const d = new Date(hoje.getFullYear(), hoje.getMonth() + i, 1);
      const mes = (d.getMonth() + 1).toString().padStart(2, '0');
      const ano = String(d.getFullYear()).slice(-2);
      lista.push(`${mes}/${ano}`);
    }
    return lista;
  }, []);

  // Ao mudar de cliente, seleciona automaticamente o mês atual (início e fim iguais)
  useEffect(() => {
    if (clienteSelecionado) {
      const hoje = new Date();
      const mesAtual = `${(hoje.getMonth() + 1).toString().padStart(2, '0')}/${String(hoje.getFullYear()).slice(-2)}`;
      setCompetenciaInicio(mesAtual);
      setCompetenciaFim(mesAtual);
    } else {
      setCompetenciaInicio('');
      setCompetenciaFim('');
    }
  }, [clienteSelecionado?.numero_dominio]);

  // Derivados do cliente selecionado
  const regime  = clienteSelecionado?.regime  || '';
  const empresa = clienteSelecionado?.empresa || '';
  const numeroDominio = clienteSelecionado?.numero_dominio || '';

  // Modal de aviso IA
  const [modalAvisoIA, setModalAvisoIA]   = useState(true);
  const [avisoConfirmado, setAvisoConfirmado] = useState(false);

  // Textos livres do cliente
  const [textosCliente, setTextosCliente] = useState([]);

  useEffect(() => {
    if (!clienteSelecionado) { setTextosCliente([]); return; }
    const token = localStorage.getItem('access');
    axios.get(`${process.env.REACT_APP_API_BASE}/api/textos-livres-contabil/`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const todos = res.data.textos || [];
      setTextosCliente(todos.filter(t => String(t.numero_dominio) === String(clienteSelecionado.numero_dominio)));
    }).catch((err) => {
      console.error('Erro ao carregar textos livres do cliente:', err);
      notificar('Não foi possível carregar as anotações do cliente.');
    });
  }, [clienteSelecionado]);

  // Gemini
  const [analisando, setAnalisando] = useState(false);
  const [mensagem, setMensagem]     = useState('');

  useEffect(() => { notificar(mensagem); }, [mensagem]);

  const voltar = () => navigate('/Contabilidade');

  const downloadBalanceteExcel = () => {
    const rows = balanceteData.map(r => ({
      codi_cta:  r.codi_cta,
      clas_cta:  r.clas_cta,
      nome_cta:  r.nome_cta,
      tipo_cta:  r.tipo_cta,
      natureza:  r.natureza,
      saldoant:  r.saldoant,
      totdeb:    r.totdeb,
      totcre:    r.totcre,
      saldoatu:  r.saldoatu,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Balancete');
    XLSX.writeFile(wb, 'balancete_parsed.xlsx');
  };

  // Conciliação
  const [somenteDiv, setSomenteDiv] = useState(false);

  // Lê pre-seleção de empresa vinda da página Contabilidade
  useEffect(() => {
    const raw = localStorage.getItem('auditoria_empresa');
    if (!raw) return;
    try {
      const obj = JSON.parse(raw);
      if (obj.numero_dominio) {
        setEmpresaPendente(obj);
        localStorage.removeItem('auditoria_empresa');
      }
    } catch {}
  }, []);

  // Busca clientes da tabela contábil
  useEffect(() => {
    setLoadingClientes(true);
    const token = localStorage.getItem('access');
    axios.get(`${process.env.REACT_APP_API_BASE}/api/empresas-contabil/`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      setClientes(res.data.empresas || []);
    }).catch((err) => {
      console.error('Erro ao carregar empresas da tabela contábil:', err);
      notificar('Não foi possível carregar a lista de empresas.');
    }).finally(() => setLoadingClientes(false));
  }, []);

  // Aplica pré-seleção quando clientes estão carregados
  useEffect(() => {
    if (!empresaPendente || clientes.length === 0) return;
    const cliente = clientes.find(c => String(c.numero_dominio) === String(empresaPendente.numero_dominio));
    if (cliente) {
      setClienteSelecionado(cliente);
      setSearchCliente(`${cliente.empresa} — Nº ${cliente.numero_dominio}`);
    }
    setEmpresaPendente(null);
  }, [empresaPendente, clientes]);

  // Fecha dropdown ao clicar fora (inclui a lista fixa)
  useEffect(() => {
    const handler = (e) => {
      const dentroWrapper = dropdownRef.current && dropdownRef.current.contains(e.target);
      const dentroLista   = listaRef.current   && listaRef.current.contains(e.target);
      if (!dentroWrapper && !dentroLista) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // position:fixed usa coords do viewport — sem scrollY
  const abrirDropdown = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
    setShowDropdown(true);
  };

  const handleBalanceteFile = (file) => {
    setBalanceteFile(file);
    setCarregando(true);
    parseXlsx(file, (data) => {
      const SALDOS = ['saldoant', 'saldoatu', 'totdeb', 'totcre'];
      const normalizado = data.map(r => {
        const n = { ...r };
        // Preserva o sinal ORIGINAL do Excel (devedor +, credor −) antes de
        // normalizar para magnitude. Esse sinal é a fonte de verdade do lado do
        // saldo — o backend o usa direto. A normalização abaixo só afeta a
        // magnitude usada na exibição e na conciliação.
        const sAnt = parseFloat(r.saldoant);
        const sAtu = parseFloat(r.saldoatu);
        n.saldoant_sig = isNaN(sAnt) ? '' : sAnt;
        n.saldoatu_sig = isNaN(sAtu) ? '' : sAtu;
        SALDOS.forEach(k => {
          const v = parseFloat(n[k]);
          if (!isNaN(v)) n[k] = Math.abs(v);
        });
        return n;
      });
      setBalanceteData(normalizado);
      setCarregando(false);
      setAbaAtiva('balancete');
    });
  };

  const handleRazaoFile = (file) => {
    setRazaoFile(file);
    setCarregando(true);
    parseXlsx(file, (data) => {
      setRazaoData(data);
      setCarregando(false);
      setAbaAtiva('razao');
    });
  };

  const hasData = balanceteData.length > 0 || razaoData.length > 0;

  const filteredBalancete = searchBalancete
    ? balanceteData.filter(r => COLS_BALANCETE.some(c => String(r[c.key] ?? '').toLowerCase().includes(searchBalancete.toLowerCase())))
    : balanceteData;

  const filteredRazao = searchRazao
    ? razaoData.filter(r => COLS_RAZAO.some(c => String(r[c.key] ?? '').toLowerCase().includes(searchRazao.toLowerCase())))
    : razaoData;

  const bAnaliticas = balanceteData.filter(r => r.tipo_cta === 'A').length;
  const bSinteticas = balanceteData.filter(r => r.tipo_cta === 'S').length;
  const bDevedoras  = balanceteData.filter(r => r.natureza === 'D').length;
  const bCredoras   = balanceteData.filter(r => r.natureza === 'C').length;
  const rLancamentos = razaoData.length;
  const rContas      = new Set(razaoData.map(r => r.nomec)).size;
  const rTotalDeb    = razaoData.reduce((s, r) => s + (parseFloat(r.valdeb) || 0), 0);
  const rTotalCre    = razaoData.reduce((s, r) => s + (parseFloat(r.valcre) || 0), 0);

  // ── Conciliação Razão × Balancete ────────────────────────
  // Regra 1: saldoant da primeira linha de cada conta, com sinal intacto.
  // Regra 2: excluir "ENCERRAMENTO DO EXERCÍCIO" (frase completa) do somatório.
  // Regra 3: cruzar por codi_cta ↔ codic (nunca por classificador — pode duplicar).
  const conciliacaoData = useMemo(() => {
    if (!balanceteData.length || !razaoData.length) return [];
    const TOL = 0.01;

    const razMap = {};
    razaoData.forEach(r => {
      const cod = String(r.codic ?? '').trim();
      if (!cod) return;

      // Regra 2: identifica encerramento do exercício pela frase completa
      const hist = String(r.historico_excel ?? r.historico ?? '');
      const isEncerramento = hist.toLowerCase().includes('encerramento do exerc');

      if (!razMap[cod]) {
        // Regra 1: primeira linha → saldoant com sinal intacto (não usar abs)
        razMap[cod] = { nome: r.nomec ?? '', saldoant: parseFloat(r.saldoant) || 0, totdeb: 0, totcre: 0 };
      }

      // Acumula movimentos, excluindo encerramento do exercício
      if (!isEncerramento) {
        razMap[cod].totdeb += parseFloat(r.valdeb) || 0;
        razMap[cod].totcre += parseFloat(r.valcre) || 0;
      }
    });

    const balCods = new Set(balanceteData.map(b => String(b.codi_cta ?? '').trim()));

    const fromBal = balanceteData.map(b => {
      const cod   = String(b.codi_cta ?? '').trim();
      const nat   = String(b.natureza ?? '').trim().toUpperCase();
      const bSatu = parseFloat(b.saldoatu) || 0;
      const rz    = razMap[cod];

      if (!rz) return { cod, nome: b.nome_cta, natureza: nat, saldoRazao: null, saldoBal: bSatu, saldoBalLabel: null, diferenca: null, status: 'SO_BALANCETE' };

      // saldoant + Σvaldeb − Σvalcre; abs só para exibição
      const saldoCalc  = rz.saldoant + rz.totdeb - rz.totcre;
      const saldoRazao = Math.abs(saldoCalc);
      const dif        = Math.round(Math.abs(bSatu - saldoRazao) * 100) / 100;

      if (dif <= TOL) {
        return { cod, nome: b.nome_cta, natureza: nat, saldoRazao, saldoBal: bSatu, saldoBalLabel: null, diferenca: dif, status: 'OK' };
      }

      const bSaldoant = parseFloat(b.saldoant) || 0;
      const bTotdeb   = parseFloat(b.totdeb)   || 0;
      const bTotcre   = parseFloat(b.totcre)   || 0;

      // Se a diferença coincide com saldoant, totdeb ou totcre do balancete,
      // o ajuste é explicado por encerramento do exercício — não é divergência real.
      const isExplicado = Math.abs(dif - bSaldoant) <= TOL
                       || Math.abs(dif - bTotdeb)   <= TOL
                       || Math.abs(dif - bTotcre)   <= TOL;

      if (isExplicado) {
        return { cod, nome: b.nome_cta, natureza: nat, saldoRazao, saldoBal: bSatu, saldoBalLabel: null, diferenca: dif, status: 'ENCERRAMENTO' };
      }

      // Nova regra: saldoRazao + créditos do razão fecha com algum campo do balancete
      if (rz.totcre > TOL) {
        const saldoComCredito = Math.round((saldoRazao + rz.totcre) * 100) / 100;
        if (Math.abs(saldoComCredito - bSatu)     <= TOL ||
            Math.abs(saldoComCredito - bTotdeb)   <= TOL ||
            Math.abs(saldoComCredito - bSaldoant) <= TOL ||
            Math.abs(saldoComCredito - bTotcre)   <= TOL) {
          return { cod, nome: b.nome_cta, natureza: nat, saldoRazao, saldoBal: saldoComCredito, saldoBalLabel: 'c/ créditos', diferenca: 0, status: 'CREDITO' };
        }
      }

      // DIVERGÊNCIA real: exibe o campo do balancete mais próximo do saldo razão
      // para que o usuário identifique rapidamente a origem sem abrir o arquivo.
      const refs = [
        { val: bSaldoant, label: 'saldo ant.' },
        { val: bTotdeb,   label: 'tot. déb.'  },
        { val: bTotcre,   label: 'tot. créd.' },
        { val: bSatu,     label: 'saldo atu.' },
      ].filter(f => f.val > TOL);

      const closestRef = refs.length
        ? refs.reduce((best, c) => Math.abs(c.val - saldoRazao) < Math.abs(best.val - saldoRazao) ? c : best)
        : { val: bSatu, label: 'saldo atu.' };

      const difRef = Math.round(Math.abs(closestRef.val - saldoRazao) * 100) / 100;

      return { cod, nome: b.nome_cta, natureza: nat, saldoRazao,
               saldoBal: closestRef.val, saldoBalLabel: closestRef.label,
               diferenca: difRef, status: 'DIVERGENCIA' };
    });

    const fromRaz = Object.entries(razMap)
      .filter(([cod]) => !balCods.has(cod))
      .map(([cod, rz]) => ({
        cod, nome: rz.nome, natureza: '-',
        saldoRazao: Math.abs(rz.saldoant + rz.totdeb - rz.totcre),
        saldoBal: null, saldoBalLabel: null, diferenca: null, status: 'SO_RAZAO',
      }));

    return [...fromBal, ...fromRaz];
  }, [balanceteData, razaoData]);

  const concOk           = conciliacaoData.filter(r => r.status === 'OK').length;
  const concDiv          = conciliacaoData.filter(r => r.status === 'DIVERGENCIA').length;
  const concSemRazao     = conciliacaoData.filter(r => r.status === 'SO_BALANCETE').length;
  const concSoRazao      = conciliacaoData.filter(r => r.status === 'SO_RAZAO').length;
  const concEncerramento = conciliacaoData.filter(r => r.status === 'ENCERRAMENTO').length;
  const concCredito      = conciliacaoData.filter(r => r.status === 'CREDITO').length;
  const concFiltered     = somenteDiv ? conciliacaoData.filter(r => r.status === 'DIVERGENCIA') : conciliacaoData;

  const imprimirConciliacao = () => {
    const statusLabel = { OK: 'OK', DIVERGENCIA: 'Alerta', CREDITO: 'Por Créditos', ENCERRAMENTO: 'Encerramento', SO_BALANCETE: 'Só Balancete', SO_RAZAO: 'Só Razão' };
    const statusColor = { OK: '#198754', DIVERGENCIA: '#dc3545', CREDITO: '#6f42c1', ENCERRAMENTO: '#6c757d', SO_BALANCETE: '#ffc107', SO_RAZAO: '#0dcaf0' };
    const rows = conciliacaoData.map(r => `
      <tr style="background:${r.status==='DIVERGENCIA'?'#fde8ea':r.status==='CREDITO'?'#f3eeff':r.status==='ENCERRAMENTO'?'#f0f0f0':r.status==='SO_BALANCETE'?'#fff9e6':r.status==='SO_RAZAO'?'#e8f7fa':'#f6fff8'}">
        <td style="font-family:monospace;color:#666;font-size:.78rem">${escapeHtml(r.cod||'-')}</td>
        <td>${escapeHtml(r.nome||'')}</td>
        <td style="text-align:center"><span style="background:${r.natureza==='D'?'#dc3545':r.natureza==='C'?'#198754':'#adb5bd'};color:#fff;padding:1px 6px;border-radius:4px;font-size:.75rem">${escapeHtml(r.natureza||'-')}</span></td>
        <td style="text-align:right;font-family:monospace">${r.saldoRazao!==null?fmtCurrency(r.saldoRazao):'-'}</td>
        <td style="text-align:right;font-family:monospace;font-weight:600">
          ${r.saldoBal!==null?fmtCurrency(r.saldoBal):'-'}
          ${r.saldoBalLabel?`<br><span style="font-size:.68rem;color:#888;font-weight:normal">${escapeHtml(r.saldoBalLabel)}</span>`:''}
        </td>
        <td style="text-align:right;font-family:monospace;color:${r.status==='DIVERGENCIA'&&r.diferenca>0.01?'#dc3545':'#6c757d'};font-weight:${r.status==='DIVERGENCIA'&&r.diferenca>0.01?'700':'400'}">${r.diferenca!==null?fmtCurrency(r.diferenca):'-'}</td>
        <td style="text-align:center"><span style="background:${statusColor[r.status]||'#adb5bd'};color:${r.status==='SO_BALANCETE'?'#000':'#fff'};padding:2px 8px;border-radius:4px;font-size:.75rem">${statusLabel[r.status]||escapeHtml(r.status)}</span></td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8">
      <title>Conciliação Razão × Balancete — ${escapeHtml(empresa||'Empresa')}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:Arial,sans-serif;font-size:12px;color:#222;padding:20px}
        h2{font-size:15px;margin-bottom:4px}
        .sub{font-size:11px;color:#555;margin-bottom:14px}
        .resumo{display:flex;gap:16px;margin-bottom:16px;flex-wrap:wrap}
        .card{border:1px solid #dee2e6;border-radius:6px;padding:8px 14px;min-width:110px;text-align:center}
        .card b{display:block;font-size:15px}
        .card small{font-size:10px;color:#888;text-transform:uppercase}
        table{width:100%;border-collapse:collapse;font-size:11px}
        th{background:#212529;color:#fff;padding:5px 8px;text-align:left}
        th.r{text-align:right} th.c{text-align:center}
        td{padding:4px 8px;border-bottom:1px solid #e9ecef}
        @media print{body{padding:10px}@page{margin:1cm;size:landscape}}
      </style></head><body>
      <h2>Conciliação Razão Contábil × Balancete</h2>
      <div class="sub">
        ${empresa ? `<strong>${escapeHtml(empresa)}</strong>${numeroDominio ? ` — Nº ${escapeHtml(numeroDominio)}` : ''}${regime ? ` | ${escapeHtml(regime)}` : ''} | ` : ''}
        Período: ${periodoInicio ? periodoInicio.split('-').reverse().join('/') : '—'} a ${periodoFim ? periodoFim.split('-').reverse().join('/') : '—'}
        &nbsp;|&nbsp; Impresso em: ${new Date().toLocaleString('pt-BR')}
      </div>
      <div class="resumo">
        <div class="card"><b style="color:#198754">${concOk}</b><small>OK</small></div>
        <div class="card"><b style="color:#dc3545">${concDiv}</b><small>Alertas</small></div>
        <div class="card"><b style="color:#6c757d">${concEncerramento}</b><small>Encerramento</small></div>
        <div class="card"><b style="color:#ffc107">${concSemRazao}</b><small>Só Balancete</small></div>
        <div class="card"><b style="color:#0dcaf0">${concSoRazao}</b><small>Só Razão</small></div>
        <div class="card"><b>${conciliacaoData.length}</b><small>Total</small></div>
      </div>
      <table>
        <thead><tr>
          <th>Cód.</th><th>Conta</th><th class="c">Nat.</th>
          <th class="r">Saldo Razão</th><th class="r">Saldo Balancete</th>
          <th class="r">Diferença</th><th class="c">Status</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </body></html>`;

    const win = window.open('', '_blank', 'width=1100,height=750');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };

  // ── Envia para Gemini ─────────────────────────────────────
  const analisarComIA = async () => {
    if (!balanceteData.length) {
      setMensagem('Faça o upload do balancete antes de analisar.');
      return;
    }
    if (!clienteSelecionado) {
      setMensagem('Selecione o cliente antes de analisar.');
      return;
    }
    if (!competenciaInicio || !competenciaFim) {
      setMensagem('Selecione a competência inicial e final antes de analisar.');
      return;
    }

    setAnalisando(true);
    setMensagem('Analisando com IA... A análise pode levar vários minutos. Você será notificado quando concluir.');

    const accessToken = localStorage.getItem('access');
    const fmtData = (d) => { const [a, m, dia] = d.split('-'); return `${dia}/${m}/${a}`; };
    const periodo = `${fmtData(periodoInicio)} a ${fmtData(periodoFim)}`;

    // Envia só as colunas necessárias para reduzir o payload.
    // saldoant_sig/saldoatu_sig levam o sinal ORIGINAL do Excel (devedor +,
    // credor −) — fonte de verdade do lado do saldo usada pelo backend.
    // codi_cta é obrigatório: é o código que o backend anexa aos alertas
    // (coluna Cód. no resultado, histórico e e-mail).
    const colsB = ['codi_cta','nome_cta','tipo_cta','clas_cta','natureza','saldoant','totdeb','totcre','saldoatu','saldoant_sig','saldoatu_sig'];
    const colsR = ['nomec','datalan','historico_excel','valdeb','valcre','natureza'];

    const balancetePayload = balanceteData.map(r => {
      const o = {};
      colsB.forEach(k => {
        if (k === 'saldoant' || k === 'saldoatu') {
          const n = parseFloat(r[k]);
          o[k] = isNaN(n) ? (r[k] ?? '') : Math.abs(n);
        } else {
          o[k] = r[k] ?? '';   // saldoant_sig/saldoatu_sig passam com o sinal
        }
      });
      return o;
    });

    // Limita razão a 500 entradas para não sobrecarregar o contexto do Gemini
    const MAX_RAZAO = 500;
    const razaoParaEnviar = razaoData.length > MAX_RAZAO
      ? razaoData.slice(-MAX_RAZAO)
      : razaoData;

    const razaoPayload = razaoParaEnviar.map(r => {
      const o = {};
      colsR.forEach(k => {
        if (k === 'datalan') {
          o[k] = r[k] instanceof Date ? r[k].toLocaleDateString('pt-BR') : (r[k] ?? '');
        } else {
          o[k] = r[k] ?? '';
        }
      });
      return o;
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/auditoria-balancete/`,
        {
          balancete:       balancetePayload,
          razao:           razaoPayload,
          regime,
          periodo,
          empresa,
          numero_dominio:  numeroDominio,
        },
        { headers: { Authorization: `Bearer ${accessToken}` }, timeout: 480000 }
      );

      if (response.data.success) {
        navigate('/auditoria-resultado', { state: { auditoria: response.data.auditoria } });
      } else {
        setMensagem(`Erro: ${response.data.erro}`);
      }
    } catch (err) {
      const msg = err.response?.data?.erro || err.message;
      setMensagem(`Erro ao analisar: ${msg}`);
    } finally {
      setAnalisando(false);
    }
  };

  return (
    <div className="das-page">

      {/* ===== MODAL AVISO IA ===== */}
      {modalAvisoIA && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 99999, backdropFilter: 'blur(3px)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>

              {/* Header */}
              <div className="modal-header py-4" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div className="w-100 text-center">
                  <h4 className="fw-bold mb-0" style={{ color: '#212529' }}>Auditoria Assistida por Inteligência Artificial</h4>
                  <small style={{ color: '#6c757d' }}>Leia com atenção antes de continuar</small>
                </div>
              </div>

              {/* Body */}
              <div className="modal-body px-4 py-4">
                <div className="d-flex flex-column gap-3">

                  <div className="p-3 rounded-3 d-flex gap-3 align-items-start" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
                    <i className="fas fa-exclamation-triangle fa-lg text-warning mt-1 flex-shrink-0"></i>
                    <div>
                      <strong className="d-block mb-1">Esta análise é realizada por Inteligência Artificial</strong>
                      <span className="text-dark" style={{ fontSize: '0.92rem' }}>
                        O resultado gerado é uma assistência tecnológica ao trabalho contábil. A IA pode cometer erros, omitir informações ou interpretar dados incorretamente. <strong>Não substitui a revisão e o julgamento técnico de um profissional contábil habilitado.</strong>
                      </span>
                    </div>
                  </div>

                  <div className="p-3 rounded-3 d-flex gap-3 align-items-start" style={{ background: '#d1ecf1', border: '1px solid #0dcaf0' }}>
                    <i className="fas fa-user-tie fa-lg text-info mt-1 flex-shrink-0"></i>
                    <div>
                      <strong className="d-block mb-1">Responsabilidade do Operador</strong>
                      <span className="text-dark" style={{ fontSize: '0.92rem' }}>
                        A responsabilidade pela conferência, validação e entrega do balancete <strong>continua integralmente atrelada ao operador responsável pelo cliente.</strong> O uso desta ferramenta não transfere nem reduz essa responsabilidade.
                      </span>
                    </div>
                  </div>

                  <div className="p-3 rounded-3 d-flex gap-3 align-items-start" style={{ background: '#d4edda', border: '1px solid #28a745' }}>
                    <i className="fas fa-check-circle fa-lg text-success mt-1 flex-shrink-0"></i>
                    <div>
                      <strong className="d-block mb-1">Como usar corretamente</strong>
                      <span className="text-dark" style={{ fontSize: '0.92rem' }}>
                        Utilize o resultado da auditoria como um <strong>ponto de partida</strong> para sua análise. Confirme manualmente os itens apontados, especialmente os de severidade <span className="badge bg-danger">GRAVE</span> e <span className="badge bg-warning text-dark">MÉDIO</span>.
                      </span>
                    </div>
                  </div>

                </div>

                {/* Checkbox confirmação */}
                <div className="mt-4 p-3 rounded-3 d-flex align-items-center gap-3" style={{ background: '#f8f9fa', border: '2px solid #dee2e6', cursor: 'pointer' }}
                  onClick={() => setAvisoConfirmado(v => !v)}>
                  <input
                    type="checkbox"
                    id="chk-aviso-ia"
                    className="form-check-input mt-0 flex-shrink-0"
                    style={{ width: '1.3rem', height: '1.3rem', cursor: 'pointer' }}
                    checked={avisoConfirmado}
                    onChange={e => { e.stopPropagation(); setAvisoConfirmado(v => !v); }}
                  />
                  <label htmlFor="chk-aviso-ia" style={{ cursor: 'pointer', fontSize: '0.92rem', fontWeight: '500' }}>
                    Estou ciente de que esta auditoria é assistida por IA, que não substitui minha responsabilidade profissional como operador, e que devo validar os resultados apresentados.
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer border-0 px-4 pb-4 pt-0 justify-content-center">
                <button
                  className="btn btn-lg px-5"
                  disabled={!avisoConfirmado}
                  onClick={() => setModalAvisoIA(false)}
                  style={{
                    background: avisoConfirmado ? 'linear-gradient(135deg, #1a3a6b 0%, #2d6a9f 100%)' : '#adb5bd',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '10px',
                    transition: 'all 0.2s',
                  }}
                >
                  <i className="fas fa-check me-2"></i>
                  Entendi, continuar para a Auditoria
                </button>
              </div>

            </div>
          </div>
        </div>
      )}

      <div className="container-fluid mt-4">

        {/* ===== HEADER ===== */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="mb-4">
                      <span className="badge bg-light text-dark me-2" style={{ fontSize: '20px', fontWeight: '500' }}>
                        Auditoria Contábil — Balancete e Razão
                      </span>
                      <span className="badge bg-warning text-dark" style={{ fontSize: '15px', fontWeight: '500' }}>
                        <i className="fas fa-robot me-1"></i>
                        Inteligência Artificial
                      </span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-search-dollar header-icon me-2"></i>
                      <h2 className="titulo-pagina m-0">Auditoria de Balancete e Razão Contábil</h2>
                    </div>
                    <p className="mb-1 opacity-75">
                      <i className="fas fa-robot me-2"></i>
                      Análise inteligente com IA — checklist auditorial, severidades GRAVE / MÉDIO / BAIXO
                    </p>
                    {(balanceteData.length > 0 || razaoData.length > 0) && (
                      <small className="opacity-75 d-block mt-1">
                        {balanceteData.length > 0 && <><i className="fas fa-check-circle me-1"></i>Balancete: <strong>{balanceteData.length} contas</strong></>}
                        {balanceteData.length > 0 && razaoData.length > 0 && ' | '}
                        {razaoData.length > 0 && <><i className="fas fa-check-circle me-1"></i>Razão: <strong>{razaoData.length} lançamentos</strong></>}
                      </small>
                    )}
                  </div>
                  <div className="mb-2">
                    <button
                      className="btn btn-sm"
                      onClick={voltar}
                      style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' }}
                    >
                      <i className="fas fa-arrow-left me-1"></i>
                      Voltar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* ===== CARD 1: CLIENTE + PERÍODO ===== */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h5 className="card-title mb-0">
                  <i className="fas fa-building me-2 text-primary"></i>Cliente e Período de Referência
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3 align-items-start">
                  <div className="col-md-5" ref={dropdownRef}>
                    <label className="form-label fw-semibold">
                      <i className="fas fa-building me-1 text-primary"></i>Cliente <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-light">
                        {loadingClientes
                          ? <span className="spinner-border spinner-border-sm text-primary"></span>
                          : <i className="fas fa-search text-muted"></i>}
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Buscar empresa ou nº domínio..."
                        value={clienteSelecionado ? `${clienteSelecionado.empresa} — Nº ${clienteSelecionado.numero_dominio}` : searchCliente}
                        onChange={(e) => { setClienteSelecionado(null); setSearchCliente(e.target.value); abrirDropdown(); }}
                        onFocus={abrirDropdown}
                      />
                      {clienteSelecionado && (
                        <button className="btn btn-outline-secondary" type="button"
                          onClick={() => { setClienteSelecionado(null); setSearchCliente(''); }}>
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>

                    {/* Portal: renderiza no <body> — imune a qualquer stacking context */}
                    {showDropdown && !clienteSelecionado && ReactDOM.createPortal(
                      <div ref={listaRef} style={{
                        position: 'fixed',
                        zIndex: 99999,
                        background: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '0.375rem',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                        top:   dropdownPos.top,
                        left:  dropdownPos.left,
                        width: dropdownPos.width,
                        maxHeight: '280px',
                        overflowY: 'auto',
                      }}>
                        {clientes
                          .filter(c => {
                            const q = searchCliente.toLowerCase();
                            return !q || (c.empresa || '').toLowerCase().includes(q) || String(c.numero_dominio || '').includes(q);
                          })
                          .slice(0, 60)
                          .map((c, i) => (
                            <div key={i}
                              className="px-3 py-2 d-flex justify-content-between align-items-center"
                              style={{ cursor: 'pointer', borderBottom: '1px solid #f8f9fa', fontSize: '0.875rem' }}
                              onMouseDown={() => { setClienteSelecionado(c); setShowDropdown(false); setSearchCliente(''); }}
                              onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                              onMouseLeave={e => e.currentTarget.style.background = ''}
                            >
                              <span className="fw-semibold text-dark">{c.empresa}</span>
                              <span className="badge bg-secondary ms-2">{c.numero_dominio}</span>
                            </div>
                          ))}
                        {clientes.filter(c => {
                          const q = searchCliente.toLowerCase();
                          return !q || (c.empresa || '').toLowerCase().includes(q) || String(c.numero_dominio || '').includes(q);
                        }).length === 0 && (
                          <div className="px-3 py-3 text-muted text-center" style={{ fontSize: '0.875rem' }}>
                            <i className="fas fa-search me-1"></i>Nenhum cliente encontrado
                          </div>
                        )}
                      </div>,
                      document.body
                    )}
                  </div>

                  {/* Competência / Período de Referência (intervalo) */}
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">
                      <i className="fas fa-calendar-alt me-1 text-primary"></i>Competência <span className="text-danger">*</span>
                    </label>
                    <div className="row g-2">
                      <div className="col-6">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '0.72rem' }}>Inicial</label>
                        <select
                          className="form-select"
                          value={competenciaInicio}
                          onChange={(e) => setCompetenciaInicio(e.target.value)}
                          title="Competência inicial"
                        >
                          <option value="">— Inicial —</option>
                          {opcoesCompetencia.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-6">
                        <label className="form-label text-muted mb-1" style={{ fontSize: '0.72rem' }}>Final</label>
                        <select
                          className="form-select"
                          value={competenciaFim}
                          onChange={(e) => setCompetenciaFim(e.target.value)}
                          title="Competência final"
                        >
                          <option value="">— Final —</option>
                          {opcoesCompetencia.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {competenciaInicio && competenciaFim && (
                      <small className="text-muted">
                        <i className="fas fa-info-circle me-1"></i>
                        {periodoInicio.split('-').reverse().join('/')} a {periodoFim.split('-').reverse().join('/')}
                      </small>
                    )}
                  </div>

                  {/* Info do cliente selecionado */}
                  {clienteSelecionado ? (
                    <div className="col-md-3">
                      <div className="p-3 rounded" style={{ background: '#f0f4ff', border: '1px solid #c8d8f8' }}>
                        <div className="fw-bold text-primary" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-check-circle me-1 text-success"></i>{clienteSelecionado.empresa}
                        </div>
                        <div className="d-flex flex-wrap gap-2 mt-1">
                          <small className="text-muted"><i className="fas fa-hashtag me-1"></i>Nº {clienteSelecionado.numero_dominio}</small>
                          <small className="text-muted"><i className="fas fa-file-invoice-dollar me-1"></i>{clienteSelecionado.regime || '—'}</small>
                        </div>
                        {textosCliente.length > 0 && (
                          <div className="mt-2" style={{ borderTop: '2px solid #a8c3f5', paddingTop: '10px' }}>
                            <div className="fw-bold mb-2" style={{ fontSize: '0.82rem', color: '#1a3a8f' }}>
                              <i className="fas fa-sticky-note me-1"></i>Observações ({textosCliente.length})
                            </div>
                            <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {textosCliente.map((t, i) => (
                                <div key={i} className="p-2 rounded" style={{ background: '#fffbea', border: '1px solid #f0d060', fontSize: '0.82rem', lineHeight: '1.4' }}>
                                  <div className="fw-semibold mb-1" style={{ fontSize: '0.72rem', color: '#888' }}>
                                    <i className="fas fa-clock me-1"></i>{t.data_hoje}
                                  </div>
                                  <div style={{ color: '#2c2c2c', whiteSpace: 'pre-wrap' }}>{t.texto}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="col-md-3">
                      <div className="text-muted p-3 rounded" style={{ background: '#f8f9fa', border: '1px dashed #dee2e6', fontSize: '0.8rem' }}>
                        <i className="fas fa-info-circle me-1"></i>Selecione um cliente
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== CARD 2: UPLOAD (só aparece após selecionar cliente) ===== */}
        {clienteSelecionado && <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h5 className="card-title mb-0">
                  <i className="fas fa-upload me-2 text-primary"></i>Upload dos Documentos Contábeis
                </h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <UploadZone label="Balancete" faIcon="fa-balance-scale" file={balanceteFile} onFile={handleBalanceteFile} gradientFrom="#1a3a6b" accept=".xls,.xlsx" />
                  </div>
                  <div className="col-md-6">
                    <UploadZone label="Razão Contábil" faIcon="fa-book" file={razaoFile} onFile={handleRazaoFile} gradientFrom="#6f42c1" />
                  </div>
                </div>
                {carregando && (
                  <div className="alert alert-info mt-3 mb-0 d-flex align-items-center gap-2">
                    <div className="spinner-border spinner-border-sm text-primary"></div>
                    <span>Processando arquivo Excel...</span>
                  </div>
                )}
                {/* Botão auditar — aparece quando balancete carregado */}
                {balanceteData.length > 0 && (
                  <div className="mt-4 d-flex justify-content-center">
                    <button
                      className="btn btn-primary"
                      onClick={analisarComIA}
                      disabled={analisando}
                      style={{ backgroundColor: '#00205b', borderColor: '#00205b' }}
                    >
                      {analisando ? (
                        <><span className="spinner-border spinner-border-sm me-2"></span>Analisando com IA...</>
                      ) : (
                        <><i className="fas fa-rocket me-2"></i>Auditar com IA</>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>}

        {/* ===== DADOS ===== */}
        {hasData && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom">
                  <ul className="nav nav-tabs card-header-tabs" style={{ borderBottom: 'none' }}>
                    {balanceteData.length > 0 && (
                      <li className="nav-item">
                        <button className={`nav-link${abaAtiva === 'balancete' ? ' active fw-bold' : ''}`} onClick={() => setAbaAtiva('balancete')}>
                          <i className="fas fa-balance-scale me-2"></i>Balancete
                          <span className="badge bg-primary ms-2">{balanceteData.length}</span>
                        </button>
                      </li>
                    )}
                    {razaoData.length > 0 && (
                      <li className="nav-item">
                        <button className={`nav-link${abaAtiva === 'razao' ? ' active fw-bold' : ''}`} onClick={() => setAbaAtiva('razao')}>
                          <i className="fas fa-book me-2"></i>Razão Contábil
                          <span className="badge ms-2" style={{ background: '#6f42c1' }}>{razaoData.length}</span>
                        </button>
                      </li>
                    )}
                    {balanceteData.length > 0 && razaoData.length > 0 && (
                      <li className="nav-item">
                        <button className={`nav-link${abaAtiva === 'conciliacao' ? ' active fw-bold' : ''}`} onClick={() => setAbaAtiva('conciliacao')}>
                          <i className="fas fa-link me-2"></i>Conciliação
                          {concDiv > 0
                            ? <span className="badge bg-danger ms-2">{concDiv} alerta{concDiv > 1 ? 's' : ''}</span>
                            : concEncerramento > 0
                            ? <span className="badge bg-secondary ms-2">{concEncerramento} enc.</span>
                            : <span className="badge bg-success ms-2">OK</span>}
                        </button>
                      </li>
                    )}
                  </ul>
                </div>

                <div className="card-body">
                  {/* ── ABA BALANCETE ── */}
                  {abaAtiva === 'balancete' && balanceteData.length > 0 && (
                    <>
                      <div className="row g-3 mb-4">
                        {[
                          { label: 'Total Contas', value: balanceteData.length, color: 'text-primary', icon: 'fa-list' },
                          { label: 'Analíticas',   value: bAnaliticas,          color: 'text-info',    icon: 'fa-tag' },
                          { label: 'Sintéticas',   value: bSinteticas,          color: 'text-warning', icon: 'fa-layer-group' },
                          { label: 'Dev. / Cred.', value: `${bDevedoras} / ${bCredoras}`, color: 'text-success', icon: 'fa-exchange-alt' },
                        ].map((k, i) => (
                          <div key={i} className="col-6 col-md-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body text-center py-3">
                                <i className={`fas ${k.icon} fa-lg ${k.color} mb-2`}></i>
                                <h4 className={`fw-bold ${k.color} mb-0`}>{k.value}</h4>
                                <small className="text-muted text-uppercase fw-semibold">{k.label}</small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <div className="input-group" style={{ maxWidth: '360px' }}>
                          <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                          <input type="text" className="form-control" placeholder="Buscar conta..." value={searchBalancete} onChange={(e) => setSearchBalancete(e.target.value)} />
                          {searchBalancete && <button className="btn btn-outline-secondary" onClick={() => setSearchBalancete('')}><i className="fas fa-times"></i></button>}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <small className="text-muted">Exibindo <strong>{filteredBalancete.length}</strong> de {balanceteData.length}</small>
                          <button className="btn btn-sm btn-outline-success" onClick={downloadBalanceteExcel} title="Baixar Excel">
                            <i className="fas fa-file-excel me-1"></i>Excel
                          </button>
                        </div>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-hover table-sm align-middle" style={{ fontSize: '0.855rem' }}>
                          <thead className="table-dark">
                            <tr>{COLS_BALANCETE.map(c => <th key={c.key} className={c.currency ? 'text-end' : ''}>{c.label}</th>)}</tr>
                          </thead>
                          <tbody>
                            {filteredBalancete.map((row, i) => (
                              <tr key={i} className={row.tipo_cta === 'S' ? 'table-light fw-semibold' : ''}>
                                {COLS_BALANCETE.map(col => {
                                  const val = row[col.key];
                                  if (col.key === 'tipo_cta') return <td key={col.key}><span className={`badge ${val === 'A' ? 'bg-primary' : 'bg-warning text-dark'}`}>{val === 'A' ? 'Analítica' : 'Sintética'}</span></td>;
                                  if (col.key === 'natureza') return <td key={col.key}><span className={`badge ${val === 'D' ? 'bg-danger' : 'bg-success'}`}>{val}</span></td>;
                                  if (col.currency) return <td key={col.key} className="text-end font-monospace">{fmtCurrency(val)}</td>;
                                  return <td key={col.key}>{val !== '' ? val : '-'}</td>;
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {/* ── ABA CONCILIAÇÃO ── */}
                  {abaAtiva === 'conciliacao' && (
                    <>
                      <div className="row g-3 mb-4">
                        {[
                          { label: 'OK',              value: concOk,           color: 'text-success',   icon: 'fa-check-circle' },
                          { label: 'Alertas',         value: concDiv,          color: 'text-danger',    icon: 'fa-exclamation-triangle' },
                          { label: 'Por Créditos',    value: concCredito,      color: 'text-purple',    icon: 'fa-credit-card', style: { color: '#6f42c1' } },
                          { label: 'Encerramento',    value: concEncerramento, color: 'text-secondary', icon: 'fa-calendar-check' },
                          { label: 'Só Balancete',    value: concSemRazao,     color: 'text-warning',   icon: 'fa-minus-circle' },
                          { label: 'Só Razão',        value: concSoRazao,      color: 'text-info',      icon: 'fa-plus-circle' },
                        ].map((k, i) => (
                          <div key={i} className="col-6 col-md">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body text-center py-3">
                                <i className={`fas ${k.icon} fa-lg ${k.color} mb-2`} style={k.style}></i>
                                <h4 className={`fw-bold ${k.color} mb-0`} style={k.style}>{k.value}</h4>
                                <small className="text-muted text-uppercase fw-semibold">{k.label}</small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <div className="d-flex gap-2">
                          <button
                            className={`btn btn-sm ${somenteDiv ? 'btn-danger' : 'btn-outline-danger'}`}
                            onClick={() => setSomenteDiv(v => !v)}
                          >
                            <i className="fas fa-filter me-1"></i>
                            {somenteDiv ? 'Mostrando só alertas' : 'Mostrar só alertas'}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={imprimirConciliacao}
                            title="Imprimir conciliação completa"
                          >
                            <i className="fas fa-print me-1"></i>Imprimir
                          </button>
                        </div>
                        <small className="text-muted">Exibindo <strong>{concFiltered.length}</strong> contas</small>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-hover table-sm align-middle" style={{ fontSize: '0.845rem' }}>
                          <thead className="table-dark">
                            <tr>
                              <th>Cód.</th>
                              <th>Conta</th>
                              <th>Nat.</th>
                              <th className="text-end">Saldo Razão</th>
                              <th className="text-end">Saldo Balancete</th>
                              <th className="text-end">Diferença</th>
                              <th className="text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {concFiltered.map((row, i) => {
                              const rowCls = row.status === 'DIVERGENCIA'   ? 'table-danger'
                                : row.status === 'SO_BALANCETE'  ? 'table-warning'
                                : row.status === 'SO_RAZAO'      ? 'table-info'
                                : row.status === 'ENCERRAMENTO'  ? 'table-secondary'
                                : row.status === 'CREDITO'       ? 'table-credito'
                                : '';
                              return (
                                <tr key={i} className={rowCls}>
                                  <td className="font-monospace text-muted" style={{ fontSize: '0.75rem' }}>{row.cod || '-'}</td>
                                  <td>{row.nome}</td>
                                  <td><span className={`badge ${row.natureza === 'D' ? 'bg-danger' : row.natureza === 'C' ? 'bg-success' : 'bg-secondary'}`}>{row.natureza || '-'}</span></td>
                                  <td className="text-end font-monospace">{row.saldoRazao !== null ? fmtCurrency(row.saldoRazao) : '-'}</td>
                                  <td className="text-end font-monospace fw-semibold">
                                    {row.saldoBal !== null ? fmtCurrency(row.saldoBal) : '-'}
                                    {row.saldoBalLabel && <div><small className="text-muted" style={{ fontSize: '0.68rem' }}>{row.saldoBalLabel}</small></div>}
                                  </td>
                                  <td className={`text-end font-monospace ${row.status === 'DIVERGENCIA' && row.diferenca > 0.01 ? 'text-danger fw-bold' : 'text-muted'}`}>{row.diferenca !== null ? fmtCurrency(row.diferenca) : '-'}</td>
                                  <td className="text-center">
                                    {row.status === 'OK'           && <span className="badge bg-success"><i className="fas fa-check-circle me-1"></i>OK</span>}
                                    {row.status === 'DIVERGENCIA'  && <span className="badge bg-danger"><i className="fas fa-exclamation-triangle me-1"></i>Alerta</span>}
                                    {row.status === 'CREDITO'      && <span className="badge" style={{ background: '#6f42c1', color: '#fff' }}>Por Créditos</span>}
                                    {row.status === 'ENCERRAMENTO' && <span className="badge bg-secondary">Encerramento</span>}
                                    {row.status === 'SO_BALANCETE' && <span className="badge bg-warning text-dark">Só Balancete</span>}
                                    {row.status === 'SO_RAZAO'     && <span className="badge bg-info text-dark">Só Razão</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {/* ── ABA RAZÃO ── */}
                  {abaAtiva === 'razao' && razaoData.length > 0 && (
                    <>
                      <div className="row g-3 mb-4">
                        {[
                          { label: 'Lançamentos', value: rLancamentos,           color: 'text-primary', icon: 'fa-receipt' },
                          { label: 'Contas',       value: rContas,               color: 'text-info',    icon: 'fa-book-open' },
                          { label: 'Total Débitos',value: `R$ ${fmtCurrency(rTotalDeb)}`, color: 'text-success', icon: 'fa-arrow-circle-up' },
                          { label: 'Total Créditos',value:`R$ ${fmtCurrency(rTotalCre)}`, color: 'text-danger',  icon: 'fa-arrow-circle-down' },
                        ].map((k, i) => (
                          <div key={i} className="col-6 col-md-3">
                            <div className="card border-0 shadow-sm h-100">
                              <div className="card-body text-center py-3">
                                <i className={`fas ${k.icon} fa-lg ${k.color} mb-2`}></i>
                                <h5 className={`fw-bold ${k.color} mb-0`}>{k.value}</h5>
                                <small className="text-muted text-uppercase fw-semibold">{k.label}</small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <div className="input-group" style={{ maxWidth: '360px' }}>
                          <span className="input-group-text bg-white"><i className="fas fa-search text-muted"></i></span>
                          <input type="text" className="form-control" placeholder="Buscar lançamento..." value={searchRazao} onChange={(e) => setSearchRazao(e.target.value)} />
                          {searchRazao && <button className="btn btn-outline-secondary" onClick={() => setSearchRazao('')}><i className="fas fa-times"></i></button>}
                        </div>
                        <small className="text-muted">Exibindo <strong>{filteredRazao.length}</strong> de {razaoData.length}</small>
                      </div>
                      <div className="table-responsive">
                        <table className="table table-hover table-sm align-middle" style={{ fontSize: '0.855rem' }}>
                          <thead className="table-dark">
                            <tr>{COLS_RAZAO.map(c => <th key={c.key} className={c.currency ? 'text-end' : ''}>{c.label}</th>)}</tr>
                          </thead>
                          <tbody>
                            {filteredRazao.map((row, i) => (
                              <tr key={i}>
                                {COLS_RAZAO.map(col => {
                                  const val = row[col.key];
                                  if (col.key === 'natureza') return <td key={col.key}><span className={`badge ${val === 'D' ? 'bg-danger' : 'bg-success'}`}>{val}</span></td>;
                                  if (col.currency) return <td key={col.key} className="text-end font-monospace">{fmtCurrency(val)}</td>;
                                  if (col.date) return <td key={col.key}>{fmtDate(val)}</td>;
                                  return <td key={col.key}>{val !== '' ? val : '-'}</td>;
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ===== EMPTY STATE ===== */}
        {!hasData && !carregando && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5">
                  <i className="fas fa-search-dollar fa-4x text-muted mb-4" style={{ opacity: 0.35 }}></i>
                  <h5 className="text-muted">Nenhum arquivo carregado</h5>
                  <p className="text-muted mb-0">Faça o upload do <strong>balancete</strong> e/ou do <strong>razão contábil</strong> para iniciar</p>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>

    </div>
  );
};

export default Auditoria_balancete;
